import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Brain, ClipboardList, Copy, Download, FilePlus2, FileSignature, Link, Pill, Plus, Printer, RotateCcw, Save, Send, Star, Trash2 } from 'lucide-react';
import { MedicalFormRenderer } from '../../components/MedicalFormRenderer';
import { useApiClient } from '../../lib/apiClient';
import { useI18n } from '../../i18n/LanguageContext';
import {
  MedicalFormDefinition,
  MedicalFormField,
  MedicalFormFieldType,
  MedicalFormTemplate,
  MedicalFormTemplateSettings,
  MedicalFormTemplateStatus,
} from '../../types/medicalForms';

type EditableTemplate = {
  id?: string;
  code: string;
  name: string;
  description: string;
  category: string;
  status: MedicalFormTemplateStatus;
  version: number;
  locale: string;
  estimatedMinutes: number | null;
  tagsText: string;
  definition: MedicalFormDefinition;
  settings: MedicalFormTemplateSettings;
};

const fieldTypeOptions: Array<{ label: string; value: MedicalFormFieldType }> = [
  { label: 'Text', value: 'text' },
  { label: 'Text Area', value: 'textarea' },
  { label: 'Number', value: 'number' },
  { label: 'Date', value: 'date' },
  { label: 'Date-Time', value: 'datetime' },
  { label: 'Time', value: 'time' },
  { label: 'Email', value: 'email' },
  { label: 'Phone', value: 'phone' },
  { label: 'URL', value: 'url' },
  { label: 'Select', value: 'select' },
  { label: 'Multi-Select', value: 'multiselect' },
  { label: 'Radio', value: 'radio' },
  { label: 'Checkbox', value: 'checkbox' },
  { label: 'Switch', value: 'switch' },
  { label: 'Instruction', value: 'instruction' },
  { label: 'Rating', value: 'rating' },
  { label: 'Scale', value: 'scale' },
  { label: 'Matrix', value: 'matrix' },
  { label: 'Signature', value: 'signature' },
  { label: 'File Upload', value: 'file' },
];

function createId(prefix: string) {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return `${prefix}-${crypto.randomUUID().slice(0, 8)}`;
  }
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
}

function defaultField(type: MedicalFormFieldType = 'text'): MedicalFormField {
  const withOptions = ['select', 'multiselect', 'radio', 'checkbox'].includes(type);
  return {
    id: createId('field'),
    type,
    label: 'New Field',
    helpText: '',
    placeholder: '',
    required: false,
    options: withOptions ? [{ label: 'Option 1', value: 'option_1' }] : [],
    linkedField: '',
    defaultValue: undefined,
    matrix: type === 'matrix'
      ? {
          inputType: 'radio',
          rows: [
            { label: 'Existing or recent illness', value: 'existing_recent_illness' },
            { label: 'Hospitalisation or surgeries', value: 'hospitalisation_surgeries' },
            { label: 'Medications', value: 'medications' },
            { label: 'Any known allergies', value: 'known_allergies' },
          ],
          columns: [
            { label: 'Yes', value: 'yes' },
            { label: 'No', value: 'no' },
            { label: 'N/A', value: 'na' },
          ],
        }
      : undefined,
    validation: {},
    layout: { width: 'full' },
  };
}

function defaultTemplate(): EditableTemplate {
  return {
    code: '',
    name: '',
    description: '',
    category: '',
    status: 'DRAFT',
    version: 1,
    locale: 'en',
    estimatedMinutes: 10,
    tagsText: '',
    definition: {
      version: 1,
      introText: '',
      sections: [
        {
          id: createId('section'),
          title: 'Section 1',
          description: '',
          fields: [defaultField('text')],
        },
      ],
    },
    settings: {
      allowPatientDraftSave: true,
      allowPatientEditsAfterSubmit: false,
      requirePatientSignature: false,
      allowFileUploads: false,
      requestBeforeAppointment: false,
      formType: 'intake',
      associatedServiceCodes: [],
    },
  };
}

function toEditable(template: MedicalFormTemplate): EditableTemplate {
  return {
    id: template.id,
    code: template.code,
    name: template.name,
    description: template.description || '',
    category: template.category || '',
    status: template.status,
    version: template.version,
    locale: template.locale || 'en',
    estimatedMinutes: template.estimatedMinutes ?? 10,
    tagsText: (template.tags || []).join(', '),
    definition: template.definition,
    settings: template.settings || {},
  };
}

export default function SettingsMedicalForms() {
  const navigate = useNavigate();
  const { fetchJson, fetchApi } = useApiClient();
  const { language, tr } = useI18n();
  const tm = (phrase: string) => language === 'it'
    ? ({
        'Medical Forms Builder': 'Builder moduli medici',
        'Create reusable intake and clinical forms, then assign them to patients.':
          'Crea moduli intake e clinici riutilizzabili, poi assegnali ai pazienti.',
        'New Template': 'Nuovo template',
        'Save Template': 'Salva template',
        'Search templates...': 'Cerca template...',
        'Loading templates...': 'Caricamento template...',
        'No templates found.': 'Nessun template trovato.',
      } as Record<string, string>)[phrase] || tr(phrase)
    : tr(phrase);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState<MedicalFormTemplate[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState<EditableTemplate>(defaultTemplate());
  const [previewAnswers, setPreviewAnswers] = useState<Record<string, unknown>>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [favoriteTemplateIds, setFavoriteTemplateIds] = useState<string[]>([]);
  const [defaultIntakeEnabled, setDefaultIntakeEnabled] = useState(true);
  const [sendBeforeHours, setSendBeforeHours] = useState(24);
  const [drugCatalog, setDrugCatalog] = useState([
    { name: 'Amoxicillin', strength: '500mg', controlled: false },
    { name: 'Codeine phosphate', strength: '30mg', controlled: true },
  ]);
  const [prescriptionDashboard, setPrescriptionDashboard] = useState([
    { id: 'rx-1', drug: 'Amoxicillin', patient: 'Jane Tester', status: 'Ready to print' },
    { id: 'rx-2', drug: 'Codeine phosphate', patient: 'Alex Adams', status: 'FP10 compliance check' },
  ]);

  const filteredTemplates = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter((item) => {
      return (
        item.name.toLowerCase().includes(q) ||
        item.code.toLowerCase().includes(q) ||
        (item.category || '').toLowerCase().includes(q) ||
        (item.description || '').toLowerCase().includes(q)
      );
    });
  }, [search, templates]);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchJson<MedicalFormTemplate[]>('/medical-forms/templates?includeArchived=true');
      setTemplates(data);
      if (!selectedId && data.length) {
        setSelectedId(data[0].id);
        setDraft(toEditable(data[0]));
      }
    } catch (err: any) {
      setError(err.message || 'Unable to load medical forms.');
    } finally {
      setLoading(false);
    }
  }, [fetchJson, selectedId]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  function selectTemplate(template: MedicalFormTemplate) {
    setSelectedId(template.id);
    setDraft(toEditable(template));
    setPreviewAnswers({});
    setError(null);
  }

  function addSection() {
    setDraft((prev) => ({
      ...prev,
      definition: {
        ...prev.definition,
        sections: [
          ...prev.definition.sections,
          {
            id: createId('section'),
            title: `Section ${prev.definition.sections.length + 1}`,
            description: '',
            fields: [],
          },
        ],
      },
    }));
  }

  function updateSection(sectionId: string, patch: Partial<MedicalFormDefinition['sections'][number]>) {
    setDraft((prev) => ({
      ...prev,
      definition: {
        ...prev.definition,
        sections: prev.definition.sections.map((section) => (section.id === sectionId ? { ...section, ...patch } : section)),
      },
    }));
  }

  function removeSection(sectionId: string) {
    setDraft((prev) => {
      const nextSections = prev.definition.sections.filter((section) => section.id !== sectionId);
      return {
        ...prev,
        definition: {
          ...prev.definition,
          sections: nextSections.length ? nextSections : [defaultTemplate().definition.sections[0]],
        },
      };
    });
  }

  function addField(sectionId: string, type: MedicalFormFieldType) {
    setDraft((prev) => ({
      ...prev,
      definition: {
        ...prev.definition,
        sections: prev.definition.sections.map((section) =>
          section.id === sectionId ? { ...section, fields: [...section.fields, defaultField(type)] } : section,
        ),
      },
    }));
  }

  function addSpecialField(sectionId: string, label: string, placeholder: string, type: MedicalFormFieldType = 'instruction') {
    const field = {
      ...defaultField(type),
      label,
      placeholder,
      helpText: placeholder,
    };
    setDraft((prev) => ({
      ...prev,
      definition: {
        ...prev.definition,
        sections: prev.definition.sections.map((section) =>
          section.id === sectionId ? { ...section, fields: [...section.fields, field] } : section,
        ),
      },
    }));
  }

  function generateSmartForm() {
    setDraft((prev) => ({
      ...prev,
      code: prev.code || 'AI-INTAKE',
      name: prev.name || 'AI Generated Intake',
      category: prev.category || 'Questionnaire',
      tagsText: prev.tagsText || 'ai, intake, questionnaire',
      definition: {
        ...prev.definition,
        introText: 'Please complete this intake before your appointment.',
        sections: [
          {
            id: createId('section'),
            title: 'Health goals',
            description: 'AI-generated starter section.',
            fields: [
              { ...defaultField('textarea'), label: 'What brings you in today?', required: true },
              { ...defaultField('checkbox'), label: 'Symptoms', options: ['Pain', 'Fatigue', 'Skin concern'].map((item) => ({ label: item, value: item.toLowerCase().replace(/\s+/g, '_') })) },
              { ...defaultField('signature'), label: 'Patient confirmation', required: true },
            ],
          },
        ],
      },
    }));
    setBanner('AI smart-form draft generated.');
  }

  function downloadTemplate() {
    const blob = new Blob([JSON.stringify(draft, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const linkEl = document.createElement('a');
    linkEl.href = url;
    linkEl.download = `${draft.code || 'medical-form-template'}.json`;
    linkEl.click();
    URL.revokeObjectURL(url);
  }

  function toggleFavoriteTemplate() {
    if (!draft.id) return;
    setFavoriteTemplateIds((current) => current.includes(draft.id!) ? current.filter((id) => id !== draft.id) : [...current, draft.id!]);
  }

  function updateField(sectionId: string, fieldId: string, patch: Partial<MedicalFormField>) {
    setDraft((prev) => ({
      ...prev,
      definition: {
        ...prev.definition,
        sections: prev.definition.sections.map((section) =>
          section.id === sectionId
            ? {
                ...section,
                fields: section.fields.map((field) => (field.id === fieldId ? { ...field, ...patch } : field)),
              }
            : section,
        ),
      },
    }));
  }

  function removeField(sectionId: string, fieldId: string) {
    setDraft((prev) => ({
      ...prev,
      definition: {
        ...prev.definition,
        sections: prev.definition.sections.map((section) =>
          section.id === sectionId ? { ...section, fields: section.fields.filter((field) => field.id !== fieldId) } : section,
        ),
      },
    }));
  }

  async function saveTemplate() {
    if (!draft.code.trim() || !draft.name.trim()) {
      setError('Code and name are required.');
      return;
    }
    setSaving(true);
    setError(null);
    setBanner(null);
    try {
      const body = {
        code: draft.code.trim(),
        name: draft.name.trim(),
        description: draft.description.trim() || undefined,
        category: draft.category.trim() || undefined,
        status: draft.status,
        version: draft.version,
        locale: draft.locale.trim() || undefined,
        estimatedMinutes: draft.estimatedMinutes || undefined,
        tags: draft.tagsText
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean),
        definition: draft.definition,
        settings: draft.settings,
      };
      const path = draft.id ? `/medical-forms/templates/${draft.id}` : '/medical-forms/templates';
      const method = draft.id ? 'PUT' : 'POST';
      const saved = await fetchJson<MedicalFormTemplate>(path, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      setBanner(draft.id ? 'Medical form template updated.' : 'Medical form template created.');
      await loadTemplates();
      setSelectedId(saved.id);
      setDraft(toEditable(saved));
    } catch (err: any) {
      setError(err.message || 'Failed to save template.');
    } finally {
      setSaving(false);
    }
  }

  async function duplicateTemplate() {
    if (!draft.id) return;
    setSaving(true);
    setError(null);
    try {
      const copy = await fetchJson<MedicalFormTemplate>(`/medical-forms/templates/${draft.id}/duplicate`, {
        method: 'POST',
      });
      await loadTemplates();
      setSelectedId(copy.id);
      setDraft(toEditable(copy));
      setBanner('Template duplicated.');
    } catch (err: any) {
      setError(err.message || 'Failed to duplicate template.');
    } finally {
      setSaving(false);
    }
  }

  async function archiveTemplate() {
    if (!draft.id) return;
    if (!window.confirm('Archive this form template?')) return;
    setSaving(true);
    setError(null);
    try {
      const response = await fetchApi(`/medical-forms/templates/${draft.id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error(`Request failed (${response.status})`);
      setBanner('Template archived.');
      setSelectedId(null);
      setDraft(defaultTemplate());
      await loadTemplates();
    } catch (err: any) {
      setError(err.message || 'Failed to archive template.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/settings')} className="p-2 rounded-full hover:bg-slate-100">
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-ink">{tm('Medical Forms Builder')}</h1>
            <p className="text-sm text-slate-500">{tm('Create reusable intake and clinical forms, then assign them to patients.')}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={generateSmartForm} className="px-3 py-2 rounded border border-slate-300 bg-white text-sm">
            <Brain size={14} className="inline mr-1" />
            AI Draft
          </button>
          <button onClick={() => window.print()} className="px-3 py-2 rounded border border-slate-300 bg-white text-sm">
            <Printer size={14} className="inline mr-1" />
            Print
          </button>
          <button onClick={downloadTemplate} className="px-3 py-2 rounded border border-slate-300 bg-white text-sm">
            <Download size={14} className="inline mr-1" />
            Download
          </button>
          <button onClick={() => { setSelectedId(null); setDraft(defaultTemplate()); }} className="px-3 py-2 rounded border border-slate-300 bg-white text-sm">
            <FilePlus2 size={14} className="inline mr-1" />
            {tm('New Template')}
          </button>
          <button onClick={saveTemplate} disabled={saving} className="px-3 py-2 rounded bg-ink text-white text-sm disabled:opacity-60">
            <Save size={14} className="inline mr-1" />
            {saving ? tr('Saving...') : tm('Save Template')}
          </button>
        </div>
      </div>

      {banner && <div className="rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">{banner}</div>}
      {error && <div className="rounded-lg border border-rose-100 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-3">
        <ClinicalControlCard title="Form automation" icon={<Send size={18} />}>
          <label className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-sm">
            <span>Default intake questionnaire</span>
            <input type="checkbox" checked={defaultIntakeEnabled} onChange={(event) => setDefaultIntakeEnabled(event.target.checked)} className="accent-ink" />
          </label>
          <label className="space-y-1 text-sm">
            <span className="text-slate-500">Send before appointment</span>
            <input type="number" min={0} className="w-full rounded-lg border border-slate-200 px-3 py-2" value={sendBeforeHours} onChange={(event) => setSendBeforeHours(Number(event.target.value))} />
          </label>
          <p className="text-xs text-slate-500">E-paper forms can be sent securely for tablet or portal completion.</p>
        </ClinicalControlCard>

        <ClinicalControlCard title="Builder modes" icon={<FileSignature size={18} />}>
          <div className="grid grid-cols-2 gap-2 text-xs">
            {['Consent form', 'Prescription form', 'Questionnaire', 'Treatment note'].map((item) => (
              <button key={item} type="button" className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-slate-700 hover:bg-slate-50" onClick={() => setDraft((prev) => ({ ...prev, category: item }))}>
                {item}
              </button>
            ))}
          </div>
          <button type="button" className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-ink" onClick={toggleFavoriteTemplate} disabled={!draft.id}>
            <Star size={14} /> {draft.id && favoriteTemplateIds.includes(draft.id) ? 'Remove favorite' : 'Favorite template'}
          </button>
        </ClinicalControlCard>

        <ClinicalControlCard title="Drug catalog & prescriptions" icon={<Pill size={18} />}>
          {drugCatalog.map((drug, index) => (
            <div key={`${drug.name}-${index}`} className="flex items-center justify-between rounded-lg border border-slate-100 bg-white px-3 py-2 text-sm">
              <span>{drug.name} {drug.strength}</span>
              <span className={`rounded-full px-2 py-0.5 text-xs ${drug.controlled ? 'bg-rose-50 text-rose-700' : 'bg-slate-100 text-slate-600'}`}>{drug.controlled ? 'Controlled' : 'Standard'}</span>
            </div>
          ))}
          <button type="button" className="rounded-lg border border-slate-200 px-3 py-2 text-sm" onClick={() => setDrugCatalog((current) => [...current, { name: `Drug ${current.length + 1}`, strength: '10mg', controlled: false }])}>
            Add drug
          </button>
        </ClinicalControlCard>

        <ClinicalControlCard title="Prescription dashboard" icon={<ClipboardList size={18} />}>
          {prescriptionDashboard.map((row) => (
            <div key={row.id} className="rounded-lg border border-slate-100 bg-white px-3 py-2 text-sm">
              <div className="font-medium text-ink">{row.drug}</div>
              <div className="text-xs text-slate-500">{row.patient} · {row.status}</div>
            </div>
          ))}
          <div className="rounded-lg border border-amber-100 bg-amber-50 p-2 text-xs text-amber-800">
            Controlled drugs require generic name, form, strength, directions, quantity in words/figures, prescriber CD PIN/Q Code, and FP10 print mode.
          </div>
        </ClinicalControlCard>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
        <div className="xl:col-span-3 rounded-xl border border-slate-200 bg-white">
          <div className="p-3 border-b border-slate-100">
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={tm('Search templates...')}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div className="max-h-[780px] overflow-auto divide-y divide-slate-100">
            {loading ? (
              <div className="p-4 text-sm text-slate-500">{tm('Loading templates...')}</div>
            ) : filteredTemplates.length ? (
              filteredTemplates.map((template) => (
                <button
                  key={template.id}
                  onClick={() => selectTemplate(template)}
                  className={`w-full text-left p-3 hover:bg-slate-50 ${selectedId === template.id ? 'bg-slate-50' : ''}`}
                >
                  <div className="font-medium text-slate-800">{template.name}</div>
                  <div className="text-xs text-slate-500">{favoriteTemplateIds.includes(template.id) ? '★ ' : ''}{template.code} • {template.status}</div>
                </button>
              ))
            ) : (
              <div className="p-4 text-sm text-slate-500">{tm('No templates found.')}</div>
            )}
          </div>
        </div>

        <div className="xl:col-span-9 space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input className="border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder="Code" value={draft.code} onChange={(event) => setDraft((prev) => ({ ...prev, code: event.target.value }))} />
              <input className="border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder="Name" value={draft.name} onChange={(event) => setDraft((prev) => ({ ...prev, name: event.target.value }))} />
              <input className="border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder="Category" value={draft.category} onChange={(event) => setDraft((prev) => ({ ...prev, category: event.target.value }))} />
              <select className="border border-slate-300 rounded-lg px-3 py-2 text-sm" value={draft.status} onChange={(event) => setDraft((prev) => ({ ...prev, status: event.target.value as MedicalFormTemplateStatus }))}>
                <option value="DRAFT">DRAFT</option>
                <option value="ACTIVE">ACTIVE</option>
                <option value="ARCHIVED">ARCHIVED</option>
              </select>
              <select className="border border-slate-300 rounded-lg px-3 py-2 text-sm" value={draft.settings.formType || 'intake'} onChange={(event) => setDraft((prev) => ({ ...prev, settings: { ...prev.settings, formType: event.target.value as any } }))}>
                <option value="intake">Intake Form</option>
                <option value="consent">Consent</option>
                <option value="treatment">Treatment Form</option>
                <option value="prescription">Prescription</option>
                <option value="lab">Lab Form</option>
                <option value="registration">Registration Form</option>
                <option value="epaper">ePaper</option>
              </select>
              <input className="border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder="Locale (e.g. en, fr, es)" value={draft.locale} onChange={(event) => setDraft((prev) => ({ ...prev, locale: event.target.value }))} />
              <input type="number" min={1} className="border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder="Estimated minutes" value={draft.estimatedMinutes ?? ''} onChange={(event) => setDraft((prev) => ({ ...prev, estimatedMinutes: event.target.value ? Number(event.target.value) : null }))} />
            </div>
            <textarea className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" rows={2} placeholder="Description" value={draft.description} onChange={(event) => setDraft((prev) => ({ ...prev, description: event.target.value }))} />
            <textarea className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" rows={2} placeholder="Intro text shown to patient before form starts" value={draft.definition.introText || ''} onChange={(event) => setDraft((prev) => ({ ...prev, definition: { ...prev.definition, introText: event.target.value } }))} />
            <input className="border border-slate-300 rounded-lg px-3 py-2 text-sm w-full" placeholder="Tags (comma separated)" value={draft.tagsText} onChange={(event) => setDraft((prev) => ({ ...prev, tagsText: event.target.value }))} />
            <div className="flex flex-wrap gap-3 text-sm text-slate-700">
              <label><input type="checkbox" checked={Boolean(draft.settings.allowPatientDraftSave)} onChange={(event) => setDraft((prev) => ({ ...prev, settings: { ...prev.settings, allowPatientDraftSave: event.target.checked } }))} /> Save draft</label>
              <label><input type="checkbox" checked={Boolean(draft.settings.allowPatientEditsAfterSubmit)} onChange={(event) => setDraft((prev) => ({ ...prev, settings: { ...prev.settings, allowPatientEditsAfterSubmit: event.target.checked } }))} /> Edit after submit</label>
              <label><input type="checkbox" checked={Boolean(draft.settings.requirePatientSignature)} onChange={(event) => setDraft((prev) => ({ ...prev, settings: { ...prev.settings, requirePatientSignature: event.target.checked } }))} /> Require signature</label>
              <label><input type="checkbox" checked={Boolean(draft.settings.allowFileUploads)} onChange={(event) => setDraft((prev) => ({ ...prev, settings: { ...prev.settings, allowFileUploads: event.target.checked } }))} /> Allow file uploads</label>
              <label><input type="checkbox" checked={Boolean(draft.settings.requestBeforeAppointment)} onChange={(event) => setDraft((prev) => ({ ...prev, settings: { ...prev.settings, requestBeforeAppointment: event.target.checked } }))} /> Request before appointment</label>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={addSection} className="px-3 py-2 rounded border border-slate-300 bg-white text-sm"><Plus size={14} className="inline mr-1" />Add Section</button>
              {draft.id && <button onClick={duplicateTemplate} className="px-3 py-2 rounded border border-slate-300 bg-white text-sm"><Copy size={14} className="inline mr-1" />Duplicate</button>}
              {draft.id && draft.status === 'ARCHIVED' && <button onClick={() => setDraft((prev) => ({ ...prev, status: 'ACTIVE' }))} className="px-3 py-2 rounded border border-slate-300 bg-white text-sm"><RotateCcw size={14} className="inline mr-1" />Restore</button>}
              {draft.id && <button onClick={archiveTemplate} className="px-3 py-2 rounded border border-rose-300 bg-rose-50 text-rose-700 text-sm"><Trash2 size={14} className="inline mr-1" />Archive</button>}
            </div>
          </div>

          <div className="space-y-3">
            {draft.definition.sections.map((section) => (
              <div key={section.id} className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <input className="border border-slate-300 rounded-lg px-3 py-2 text-sm w-full" value={section.title} onChange={(event) => updateSection(section.id, { title: event.target.value })} />
                  <button onClick={() => removeSection(section.id)} className="px-3 py-2 rounded border border-rose-300 bg-rose-50 text-rose-700 text-sm">Remove</button>
                </div>
                <textarea className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" rows={2} value={section.description || ''} onChange={(event) => updateSection(section.id, { description: event.target.value })} placeholder="Section description" />
                <div className="flex gap-2 flex-wrap">
                  {fieldTypeOptions.map((item) => (
                    <button key={`${section.id}-${item.value}`} onClick={() => addField(section.id, item.value)} className="px-2 py-1 text-xs rounded border border-slate-300 bg-white hover:bg-slate-50">
                      + {item.label}
                    </button>
                  ))}
                  <button onClick={() => addSpecialField(section.id, 'SNOMED CT code', 'Clinical terminology code and display text', 'text')} className="px-2 py-1 text-xs rounded border border-sky-300 bg-sky-50 text-sky-700">
                    + SNOMED
                  </button>
                  <button onClick={() => addSpecialField(section.id, 'Drawing / body chart', 'Draw or annotate the treatment area')} className="px-2 py-1 text-xs rounded border border-emerald-300 bg-emerald-50 text-emerald-700">
                    + Drawing
                  </button>
                  <button onClick={() => addSpecialField(section.id, 'Linked patient field', 'Pull value from demographics or clinical profile', 'text')} className="px-2 py-1 text-xs rounded border border-violet-300 bg-violet-50 text-violet-700">
                    + Linked field
                  </button>
                </div>
                <div className="space-y-2">
                  {section.fields.map((field) => (
                    <div key={field.id} className="rounded-lg border border-slate-200 p-3 space-y-2 bg-slate-50">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                        <input className="border border-slate-300 rounded px-2 py-1 text-sm" value={field.label} onChange={(event) => updateField(section.id, field.id, { label: event.target.value })} placeholder="Label" />
                        <select
                          className="border border-slate-300 rounded px-2 py-1 text-sm"
                          value={field.type}
                          onChange={(event) => {
                            const type = event.target.value as MedicalFormFieldType;
                            const defaults = defaultField(type);
                            updateField(section.id, field.id, {
                              type,
                              options: ['select', 'multiselect', 'radio', 'checkbox'].includes(type) ? field.options?.length ? field.options : defaults.options : [],
                              matrix: type === 'matrix' ? field.matrix || defaults.matrix : undefined,
                            });
                          }}
                        >
                          {fieldTypeOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                        </select>
                        <input className="border border-slate-300 rounded px-2 py-1 text-sm" value={field.id} onChange={(event) => updateField(section.id, field.id, { id: event.target.value })} placeholder="Field key" />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <input className="border border-slate-300 rounded px-2 py-1 text-sm" value={field.placeholder || ''} onChange={(event) => updateField(section.id, field.id, { placeholder: event.target.value })} placeholder="Placeholder" />
                        <input className="border border-slate-300 rounded px-2 py-1 text-sm" value={field.helpText || ''} onChange={(event) => updateField(section.id, field.id, { helpText: event.target.value })} placeholder="Help text" />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <select className="border border-slate-300 rounded px-2 py-1 text-sm" value={field.linkedField || ''} onChange={(event) => updateField(section.id, field.id, { linkedField: event.target.value || undefined })}>
                          <option value="">No linked field</option>
                          <option value="client_full_name">Client full name</option>
                          <option value="client_first_name">Client first name</option>
                          <option value="client_last_name">Client last name</option>
                          <option value="client_email">Client email</option>
                        </select>
                        <input
                          className="border border-slate-300 rounded px-2 py-1 text-sm"
                          value={typeof field.defaultValue === 'string' ? field.defaultValue : ''}
                          onChange={(event) => updateField(section.id, field.id, { defaultValue: event.target.value || undefined })}
                          placeholder="Default value or merge tag"
                        />
                      </div>
                      {['select', 'multiselect', 'radio', 'checkbox'].includes(field.type) && (
                        <textarea
                          className="w-full border border-slate-300 rounded px-2 py-1 text-sm"
                          rows={2}
                          value={(field.options || []).map((option) => `${option.label}:${option.value}`).join('\n')}
                          onChange={(event) =>
                            updateField(section.id, field.id, {
                              options: event.target.value
                                .split('\n')
                                .map((line) => line.trim())
                                .filter(Boolean)
                                .map((line) => {
                                  const [label, value] = line.split(':');
                                  const normalized = (value || label || '').trim().toLowerCase().replace(/\s+/g, '_');
                                  return { label: (label || '').trim(), value: normalized };
                                })
                                .filter((item) => item.label && item.value),
                            })
                          }
                          placeholder="Options, one per line: Label:value"
                        />
                      )}
                      {field.type === 'matrix' && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                          <select
                            className="border border-slate-300 rounded px-2 py-1 text-sm"
                            value={field.matrix?.inputType || 'radio'}
                            onChange={(event) => updateField(section.id, field.id, { matrix: { rows: field.matrix?.rows || [], columns: field.matrix?.columns || [], inputType: event.target.value as any } })}
                          >
                            <option value="radio">Single choice</option>
                            <option value="checkbox">Checkboxes</option>
                            <option value="text">Short text</option>
                            <option value="paragraph">Paragraph</option>
                          </select>
                          <textarea
                            className="border border-slate-300 rounded px-2 py-1 text-sm"
                            rows={3}
                            value={(field.matrix?.rows || []).map((option) => `${option.label}:${option.value}`).join('\n')}
                            onChange={(event) =>
                              updateField(section.id, field.id, {
                                matrix: {
                                  inputType: field.matrix?.inputType || 'radio',
                                  columns: field.matrix?.columns || [],
                                  rows: event.target.value.split('\n').map((line) => line.trim()).filter(Boolean).map((line) => {
                                    const [label, value] = line.split(':');
                                    return { label: (label || '').trim(), value: (value || label || '').trim().toLowerCase().replace(/\s+/g, '_') };
                                  }),
                                },
                              })
                            }
                            placeholder="Rows, one per line"
                          />
                          <textarea
                            className="border border-slate-300 rounded px-2 py-1 text-sm"
                            rows={3}
                            value={(field.matrix?.columns || []).map((option) => `${option.label}:${option.value}`).join('\n')}
                            onChange={(event) =>
                              updateField(section.id, field.id, {
                                matrix: {
                                  inputType: field.matrix?.inputType || 'radio',
                                  rows: field.matrix?.rows || [],
                                  columns: event.target.value.split('\n').map((line) => line.trim()).filter(Boolean).map((line) => {
                                    const [label, value] = line.split(':');
                                    return { label: (label || '').trim(), value: (value || label || '').trim().toLowerCase().replace(/\s+/g, '_') };
                                  }),
                                },
                              })
                            }
                            placeholder="Answers, one per line"
                          />
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <label className="text-xs text-slate-600">
                          <input type="checkbox" checked={Boolean(field.required)} onChange={(event) => updateField(section.id, field.id, { required: event.target.checked })} /> Required
                        </label>
                        <button onClick={() => removeField(section.id, field.id)} className="text-xs px-2 py-1 rounded border border-rose-300 bg-rose-50 text-rose-700">Delete Field</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
            <p className="font-semibold text-slate-900">Live Preview</p>
            <MedicalFormRenderer definition={draft.definition} answers={previewAnswers} onChange={(fieldId, value) => setPreviewAnswers((prev) => ({ ...prev, [fieldId]: value }))} />
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Link size={18} className="text-ink" />
              <p className="font-semibold text-slate-900">Prescription integrations</p>
            </div>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-4">
              {['DoseSpot', 'CloudRx', 'SignatureRx', 'Pharmacierge'].map((integration) => (
                <div key={integration} className="rounded-lg border border-slate-100 bg-slate-50 p-3 text-sm">
                  <div className="font-medium text-ink">{integration}</div>
                  <div className="text-xs text-slate-500">Ready to connect</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ClinicalControlCard({ title, icon, children }: { title: string; icon: ReactNode; children: ReactNode }) {
  return (
    <section className="space-y-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-2 text-ink">
        {icon}
        <h2 className="text-sm font-semibold">{title}</h2>
      </div>
      {children}
    </section>
  );
}
